import React, { useState, useRef, useEffect } from "react";

interface RydMiniWindowProps {
    children: React.ReactNode;
}

const STORAGE_KEY = "RydMiniWindowPosition";

const RydMiniWindow: React.FC<RydMiniWindowProps> = ({ children }) => {
    const windowRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 100, y: 100 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);

    // Load saved position from localStorage on mount
    useEffect(() => {
        const savedPosition = localStorage.getItem(STORAGE_KEY);
        if (savedPosition) {
            try {
                const pos = JSON.parse(savedPosition);
                setPosition({ x: pos.x, y: pos.y });
            } catch {
                // ignore parsing errors
            }
        }
    }, []);

    // Save position to localStorage when dragging ends
    useEffect(() => {
        if (!dragging) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
        }
    }, [dragging, position]);

    const onMouseDown = (e: React.MouseEvent) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
            setDragging(true);
        }
    };

    const onMouseMove = (e: MouseEvent) => {
        if (dragging) {
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y,
            });
        }
    };

    const onMouseUp = () => {
        if (dragging) {
            setDragging(false);
        }
    };

    useEffect(() => {
        if (dragging) {
            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        } else {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragging, dragOffset]);

    return (
        <div
            ref={windowRef}
            onMouseDown={onMouseDown}
            style={{
                position: "fixed",
                top: position.y,
                left: position.x,
                zIndex: 9999,
                cursor: dragging ? "grabbing" : "grab",
                padding: "10px",
                background: "#fff",
                border: "1px solid #ccc",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.3)",
                userSelect: "none",
            }}
        >
            {children}
        </div>
    );
};

export default RydMiniWindow;